import { createSelector } from "@reduxjs/toolkit";
import type { IIssueBoardApi } from "@mcapps/shared";
import { AccessLevelsEnum } from "@mcapps/shared/enums";
import type { IProjectMemberApiItem } from "@schemas/projectSchema";
import { openapi } from "../../../../openapi";
import type { IRootState } from "../../../store";

export const getIssueBoardScopedMembers = (
  projectMembers: IProjectMemberApiItem[],
  issueBoard: IIssueBoardApi | null | undefined,
): IProjectMemberApiItem[] => {
  const allMembers = Array.isArray(projectMembers) ? projectMembers : [];

  const uniqueMembersMap = new Map<string, IProjectMemberApiItem>();
  allMembers.forEach((member) => {
    const userId = member?.user?._id;
    if (member.isPassive !== true && userId != null && uniqueMembersMap.has(userId) === false) {
      uniqueMembersMap.set(userId, member);
    }
  });

  const members = Array.from(uniqueMembersMap.values());
  const access = issueBoard?.access;
  if (access == null || access.level == null || access.level === AccessLevelsEnum.PROJECT) {
    return members;
  }

  if (access.level === AccessLevelsEnum.GROUPS) {
    const allowedGroups = new Set(access.groups ?? []);
    return members.filter((member) => allowedGroups.has(member.group) || member.group === "ADMIN");
  }

  if (access.level === AccessLevelsEnum.USERS) {
    const allowedUserIds = new Set(
      (access.users ?? []).map((user) => (typeof user === "string" ? user : user?._id)).filter(Boolean),
    );

    return members.filter((member) => member.user?._id != null && allowedUserIds.has(member.user._id));
  }

  return members;
};

const EMPTY_MEMBERS: IProjectMemberApiItem[] = [];
const selectState = (state: IRootState) => state;
const selectProjectIdArgument = (_state: IRootState, projectId: string) => projectId;
const selectIssueBoardArgument = (_state: IRootState, _projectId: string, issueBoard: IIssueBoardApi) => issueBoard;

export const makeSelectIssueBoardScopedMembers = () => {
  const selectProjectMembers = createSelector(
    [selectState, selectProjectIdArgument],
    (state, projectId): IProjectMemberApiItem[] =>
      openapi.endpoints.getProjectMembers.select({ projectId })(state).data ?? EMPTY_MEMBERS,
  );

  return createSelector([selectProjectMembers, selectIssueBoardArgument], getIssueBoardScopedMembers);
};
